import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { IDetailPageState } from '../../reducers'

import {
  GenericDetailsPageClient,
  GenericDetailsPageClientProps,
  CallbackArgs
} from 'DVPAWebWidgetsDetailsPageClient'

let PurchaseFlowBootloader: typeof GenericDetailsPageClient
if (!global.__SERVER__ || global.__DEVSERVER__) {
  PurchaseFlowBootloader = require('DVPAWebWidgetsDetailsPageClient')
    .GenericDetailsPageClient
}

import {
  purchaseSelector,
  offerTokenToPurchaseActionSelector
} from '../../selectors/purchase'
import { subPageTypeSelector } from '../../selectors/detailFragment'
import { DetailPageActions } from '../../actions'
import { Dispatch } from 'redux'
import {
  purchaseCancelled,
  purchaseFailed,
  refreshAfterDone,
  triggerPurchase,
  refreshCSRF
} from '../../actions/purchase'
import { withLogger, ILogger } from '../../context/withLogger'

import { COUNTER_PACKAGE_PREFIX } from '../../utils/utils'
import { ErrorBoundary } from '../error/ErrorBoundary'
import {
  onPlayerCapabilities,
  PlayerCapabilities
} from '../../integrations/playerIntegrationAui'
import {
  refundStatusCallback,
  RefundStatus,
  refundCancelled,
  refundFailed
} from '../../actions/refund'
import { Logger, replaceState } from '@utils'
import { refundingDetails } from '../../selectors/refundFragment'
import { getSpaDeviceInfo } from '@shared'

type OwnProps = ILogger
export type IPurchaseFlowBootloader = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps

type State = {
  initialized: boolean
  supportsWebPlayback: boolean
  supportsHDMovies: boolean
}

const RESTART_PURCHASE_DELAY = 300 // To allow time for player capabilities to be retrieved
const PURCHASE_ELEMENT_ID = 'mfa-purchase-root'

const getQueryParam = (param: string) => {
  const params = window.location.search
  const result = (params || '').match(`${param}=([^&]+)`)
  return result && decodeURIComponent(result[1])
}

export const stripTokens = (url: string) =>
  url.replace(/continueToken=[^&]+&?/, '').replace(/offerToken=[^&]+&?/, '')

export class ConnectedPurchaseFlowWrapper extends React.Component<
  IPurchaseFlowBootloader
> {
  private el: HTMLDivElement | undefined
  private portalRoot: HTMLElement | null
  state: State

  constructor(props: IPurchaseFlowBootloader) {
    super(props)
    this.state = {
      initialized: false,
      supportsWebPlayback: false,
      supportsHDMovies: false
    }
    this.portalRoot = null
  }

  componentDidMount() {
    const { triggerPurchase, usePlayer } = this.props

    const setCapabilities = (capabilities: PlayerCapabilities) => {
      this.setState({
        supportsWebPlayback: Boolean(capabilities.supportsWebPlayback),
        supportsHDMovies: Boolean(capabilities.supportsHDMovies)
      })
    }

    if (!usePlayer) {
      onPlayerCapabilities(setCapabilities, this.props.logger)
    }

    if (this.shouldInitialize()) {
      this.initialize()

      const continueToken = getQueryParam('continueToken')
      const offerToken = getQueryParam('offerToken')
      if (continueToken && offerToken) {
        setTimeout(
          () => triggerPurchase(offerToken, continueToken),
          RESTART_PURCHASE_DELAY
        )

        if (window.location.href) {
          replaceState(null, document.title, stripTokens(window.location.href))
        }
      }
    }
  }

  componentDidUpdate() {
    if (!this.state.initialized && this.shouldInitialize()) {
      this.initialize()
    }
  }

  componentWillUnmount() {
    if (this.portalRoot && this.el) {
      this.portalRoot.removeChild(this.el)
    }
  }

  getPurchaseElement() {
    let el = document.getElementById(PURCHASE_ELEMENT_ID)

    if (el) {
      return el
    }

    el = document.createElement('div')
    el.setAttribute('id', PURCHASE_ELEMENT_ID)
    document.body.appendChild(el)
    return el
  }

  initialize() {
    this.portalRoot = this.getPurchaseElement()
    this.el = document.createElement('div')
    this.portalRoot.appendChild(this.el)
    this.setState({ initialized: true })
  }

  shouldInitialize = () =>
    (Object.keys(this.props.offerTokenToPurchaseAction).length > 0 &&
      this.props.useMFA) ||
    this.props.hasRefunds

  render() {
    const {
      purchasingDetails,
      refundingDetails,
      callbackCancel,
      resetAfterCancel,
      callbackFailure,
      failed,
      refreshAfterDone,
      callbackCSRF,
      callbackCancelRefund,
      callbackFailureRefund,
      callbackRefund,
      subPageType,
      titleId,
      capabilities
    } = this.props

    const { initialized, supportsWebPlayback, supportsHDMovies } = this.state

    const playerDetails = {
      supportsWebPlayback: capabilities
        ? capabilities.supportsWebPlayback
        : supportsWebPlayback,
      supportsHDMovies: capabilities
        ? capabilities.supportsHDMovies
        : supportsHDMovies,
      supportsUHD: false
    }

    if (!initialized) {
      return null
    }

    let props: GenericDetailsPageClientProps = {
      action: undefined,
      actionContext: {
        callbacks: {
          callbackCancel: () => {},
          callbackFailure: () => {}
        }
      }
    }

    if (purchasingDetails) {
      const { details, continueToken, refTag } = purchasingDetails
      const offerToken = details && details.offer.offerToken
      const csrfToken = details && details.offer.csrfTokenWorkflow
      const globalTitleIdentifier = details && details.gti
      const spaDeviceInfo = global.__NATIVEAPP__ && getSpaDeviceInfo()
      const { deviceTypeId, deviceId } = spaDeviceInfo || {}

      props = {
        action: 'purchase',
        actionContext: {
          isVisible: true,
          playerDetails,
          extraData: {
            subPageType,
            titleId,
            refTag,
            globalTitleIdentifier,
            ...(deviceTypeId && { deviceTypeId }),
            ...(deviceId && { deviceId })
          },
          callbacks: {
            callbackCancel,
            callbackFailure,
            resetAfterCancel,
            refreshAfterDone,
            failed,
            callbackCSRF
          },
          offerContext: { offerToken, csrfToken },
          continueToken
        }
      }
    } else if (refundingDetails) {
      props = {
        action: 'refund',
        actionContext: {
          isVisible: true,
          asin: refundingDetails.asin,
          csrfToken: refundingDetails.csrfToken,
          globalTitleIdentifier: refundingDetails.gti,
          callbacks: {
            callbackCancel: callbackCancelRefund,
            callbackFailure: callbackFailureRefund,
            callbackRefundSuccess: callbackRefund('Success'),
            callbackRefundFailure: callbackRefund('Failure')
          }
        }
      }
    }
    return ReactDOM.createPortal(
      <ErrorBoundary name='purchaseWorkflow'>
        <PurchaseFlowBootloader {...props} />
      </ErrorBoundary>,
      this.el!
    )
  }
}

const mapStateToProps = (state: IDetailPageState) => ({
  purchasingDetails: purchaseSelector(state),
  refundingDetails: refundingDetails(state),
  offerTokenToPurchaseAction: offerTokenToPurchaseActionSelector(state),
  hasRefunds: Object.keys(state.refund.fragments).length > 0,
  subPageType: subPageTypeSelector(state),
  titleId: state.pageTitleId,
  useMFA: state.features.useMFA,
  usePlayer: state.features.usePlayer,
  capabilities: state.player.capabilities
})

export interface CallbackInput {
  dispatch: Dispatch<DetailPageActions>
  logger: Logger
}

type callbackType = (props: CallbackInput) => unknown

const mapDispatchToProps = (
  dispatch: Dispatch<DetailPageActions>,
  { logger }: OwnProps
) => {
  const logCallback = (action: 'Purchase' | 'Refund') => (
    callback: callbackType,
    callbackName: string,
    isError: boolean = false
  ) => {
    const log = isError ? logger.error : logger.info
    log(`[${action}] - Callback ${callbackName} triggered`).count(
      `${COUNTER_PACKAGE_PREFIX}_${action}_${callbackName}`,
      1,
      true
    )
    callback({ dispatch, logger })
  }

  const logPurchase = logCallback('Purchase')
  const logRefund = logCallback('Refund')

  return {
    //Purchase Callbacks
    callbackCancel: () => logPurchase(purchaseCancelled, 'callbackCancel'),
    resetAfterCancel: () => logPurchase(purchaseCancelled, 'resetAfterCancel'),
    callbackFailure: () => logPurchase(purchaseFailed, 'callbackFailure', true),
    failed: () => logPurchase(purchaseFailed, 'failed', true),
    refreshAfterDone: () => logPurchase(refreshAfterDone, 'refreshAfterDone'),
    callbackCSRF: () => logPurchase(refreshCSRF, 'callbackCSRF'),

    //Refund Callbacks
    callbackCancelRefund: () => logRefund(refundCancelled, 'callbackCancel'),
    callbackFailureRefund: () => logRefund(refundFailed, 'callbackFailure'),
    callbackRefund: (status: RefundStatus) => (args: CallbackArgs) =>
      logRefund(refundStatusCallback(status, args), `callbackRefund${status}`),
    triggerPurchase: (offerToken: string, continueToken: string) =>
      triggerPurchase(dispatch, logger, offerToken, continueToken)
  }
}

export const PurchaseFlowWrapper = withLogger(
  connect(mapStateToProps, mapDispatchToProps)(ConnectedPurchaseFlowWrapper)
)
